import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { CampaignProvider } from './contexts/CampaignContext';
import { LicenseErrorProvider } from './contexts/LicenseErrorContext';
import { AppSettingsProvider } from './contexts/AppSettingsContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { IntentProvider } from './contexts/IntentContext';
import { IntentSocketProvider } from './contexts/IntentSocketContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from './components/layouts/AdminLayout';
import LoginForm from './components/auth/LoginForm';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import Loading from './components/shared/Loading';
import AppHead from './components/shared/AppHead';

// Lazy-loaded components
const AgentChat = lazy(() => import('./components/chat/AgentChat'));
const WhatsAppDashboard = lazy(() => import('./components/dashboard/WhatsAppDashboard'));
const MetricsDashboard = lazy(() => import('./components/dashboard/MetricsDashboard'));
const SentimentDashboard = lazy(() => import('./components/dashboard/SentimentDashboard'));
const UserManagement = lazy(() => import('./components/dashboard/UserManagement'));
const TemplateManager = lazy(() => import('./components/templates/TemplateManager'));
const TemplateForm = lazy(() => import('./components/templates/TemplateForm'));
const TemplateSender = lazy(() => import('./components/templates/TemplateSender'));
const TemplatePerformanceDashboard = lazy(() => import('./components/templates/TemplatePerformanceDashboard'));
const ActivityLog = lazy(() => import('./components/activities/ActivityLog'));
const UserProfile = lazy(() => import('./components/profile/UserProfile'));
const ConversationsReport = lazy(() => import('./components/reports/ConversationsReport'));
const MediaManagement = lazy(() => import('./components/media/MediaManagement'));
const BotFlowManagement = lazy(() => import('./components/botflow/BotFlowManagement'));
const Settings = lazy(() => import('./components/settings/Settings'));

// API Key Management components (lazy-loaded)
const ApiKeyList = lazy(() => import('./components/ApiKeyManagement').then(module => ({ default: module.ApiKeyList })));
const CreateApiKey = lazy(() => import('./components/ApiKeyManagement').then(module => ({ default: module.CreateApiKey })));
const ApiKeyDetails = lazy(() => import('./components/ApiKeyManagement').then(module => ({ default: module.ApiKeyDetails })));
const EditApiKey = lazy(() => import('./components/ApiKeyManagement').then(module => ({ default: module.EditApiKey })));

// Campaign components (lazy-loaded)
const CampaignList = lazy(() => import('./components/campaign/CampaignList'));
const CampaignForm = lazy(() => import('./components/campaign/CampaignForm'));
const CampaignDetails = lazy(() => import('./components/campaign/CampaignDetails'));

// Profile components (lazy-loaded)
const ProfilesPage = lazy(() => import('./components/profiles/ProfilesPage'));
const ProfileForm = lazy(() => import('./components/profiles/ProfileForm'));
const ProfileDetail = lazy(() => import('./components/profiles/ProfileDetail'));
const TagManagement = lazy(() => import('./components/profiles/TagManagement'));

// Profile Analytics components (lazy-loaded)
const ProfileMetricsDashboard = lazy(() => import('./components/profiles/analytics/ProfileMetricsDashboard'));
const TagEffectivenessAnalytics = lazy(() => import('./components/profiles/analytics/TagEffectivenessAnalytics'));
const CustomerJourneyVisualization = lazy(() => import('./components/profiles/analytics/CustomerJourneyVisualization'));

// Intent Detection components (lazy-loaded)
const IntentRulesManagement = lazy(() => import('./components/intent/IntentRulesManagement'));
const IntentRuleForm = lazy(() => import('./components/intent/IntentRuleForm'));
const IntentRuleDetails = lazy(() => import('./components/intent/IntentRuleDetails'));
const IntentAnalytics = lazy(() => import('./components/intent/IntentAnalytics'));

// Cache Management components (lazy-loaded)
const CacheManagement = lazy(() => import('./components/cache/CacheManagement'));

// Root redirect component
const RootRedirect = () => {
  const { user } = useAuth();
  return user.role === 'admin'
    ? <Navigate to="/admin/dashboard/overview" replace />
    : <Navigate to="/chat" replace />;
};

// Protected Route with Layout Component and Suspense
const ProtectedRoute = ({ children, layout: Layout = React.Fragment, requiredRole }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <Loading fullScreen />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/chat" />;
  }

  return (
    <Layout>
      <Suspense fallback={<Loading />}>
        {children}
      </Suspense>
    </Layout>
  );
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <Loading fullScreen />;
  }

  if (user) {
    return <Navigate to="/" />;
  }

  return children;
};

// Toast Component for notifications
const Toast = ({ message, type, onClose }) => {
  const types = {
    success: 'bg-green-100 border-green-400 text-green-700',
    error: 'bg-red-100 border-red-400 text-red-700',
    warning: 'bg-yellow-100 border-yellow-400 text-yellow-700',
    info: 'bg-blue-100 border-blue-400 text-blue-700',
  };

  return (
    <div className={`${types[type] || types.info} border px-4 py-3 rounded-lg shadow-md animate-fade-in relative max-w-md w-full`}>
      <strong className="font-bold mr-1">{type.charAt(0).toUpperCase() + type.slice(1)}:</strong>
      <span>{message}</span>
      <button
        className="absolute top-0 bottom-0 right-0 px-4 py-3"
        onClick={onClose}
      >
        <svg className="h-4 w-4 fill-current" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 8.586l3.293-3.293a1 1 0 0 1 1.414 1.414L11.414 10l3.293 3.293a1 1 0 0 1-1.414 1.414L10 11.414l-3.293 3.293a1 1 0 0 1-1.414-1.414L8.586 10 5.293 6.707a1 1 0 0 1 1.414-1.414L10 8.586z"
          />
        </svg>
      </button>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <LicenseErrorProvider>
        <AppSettingsProvider>
          <AppHead />
          <Router>
            <SocketProvider>
              <CampaignProvider>
            <Routes>
              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginForm />
                  </PublicRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <PublicRoute>
                    <ResetPassword />
                  </PublicRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <RootRedirect />
                  </ProtectedRoute>
                }
              />

              {/* User Profile Route */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <UserProfile />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/dashboard/overview"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <WhatsAppDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/dashboard/metrics"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <MetricsDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/dashboard/sentiment"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <SentimentDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard/templates"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <TemplatePerformanceDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <UserManagement />
                  </ProtectedRoute>
                }
              />

              {/* Activities Route */}
              <Route
                path="/activities"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <ActivityLog />
                  </ProtectedRoute>
                }
              />

              {/* API Key Management Routes */}
              <Route
                path="/api-keys"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <ApiKeyList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/api-keys/new"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <CreateApiKey />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/api-keys/:id"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <ApiKeyDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/api-keys/:id/edit"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <EditApiKey />
                  </ProtectedRoute>
                }
              />

              {/* Template Routes */}
              <Route
                path="/templates"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <TemplateManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/templates/create"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <TemplateForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/templates/edit/:id"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <TemplateForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/templates/send"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <TemplateSender />
                  </ProtectedRoute>
                }
              />

              {/* Media Management Route */}
              <Route
                path="/media"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <MediaManagement />
                  </ProtectedRoute>
                }
              />

              {/* Campaign Routes */}
              <Route
                path="/campaigns"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <CampaignList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/campaigns/new"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <CampaignForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/campaigns/:id"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <CampaignDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/campaigns/:id/edit"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <CampaignForm isEdit={true} />
                  </ProtectedRoute>
                }
              />

              {/* Bot Flow Route */}
              <Route
                path="/bot-flows"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <BotFlowManagement />
                  </ProtectedRoute>
                }
              />

              {/* Agent Chat */}
              <Route
                path="/chat"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <AgentChat />
                  </ProtectedRoute>
                }
              />

              {/* Conversations Report Route */}
              <Route
                path="/reports/conversations"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <ConversationsReport />
                  </ProtectedRoute>
                }
              />

              {/* Settings Route */}
              <Route
                path="/settings"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <Settings />
                  </ProtectedRoute>
                }
              />

              {/* Cache Management Route */}
              <Route
                path="/admin/cache"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <CacheManagement />
                  </ProtectedRoute>
                }
              />

              {/* Customer Profiles Routes */}
              <Route
                path="/profiles"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <ProfileProvider>
                      <ProfilesPage />
                    </ProfileProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profiles/new"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <ProfileProvider>
                      <ProfileForm />
                    </ProfileProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profiles/:id"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <ProfileProvider>
                      <ProfileDetail />
                    </ProfileProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profiles/:id/edit"
                element={
                  <ProtectedRoute layout={AdminLayout}>
                    <ProfileProvider>
                      <ProfileForm isEdit={true} />
                    </ProfileProvider>
                  </ProtectedRoute>
                }
              />
              {/* Profile Analytics Routes */}
              <Route
                path="/analytics/profiles"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <ProfileProvider>
                      <ProfileMetricsDashboard />
                    </ProfileProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/tags"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <ProfileProvider>
                      <TagEffectivenessAnalytics />
                    </ProfileProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/journey"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <ProfileProvider>
                      <CustomerJourneyVisualization />
                    </ProfileProvider>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/tags"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <ProfileProvider>
                      <TagManagement />
                    </ProfileProvider>
                  </ProtectedRoute>
                }
              />

              {/* Intent Detection Routes */}
              <Route
                path="/intent"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <IntentProvider>
                      <IntentSocketProvider>
                        <IntentRulesManagement />
                      </IntentSocketProvider>
                    </IntentProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/intent/new"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <IntentProvider>
                      <IntentSocketProvider>
                        <IntentRuleForm />
                      </IntentSocketProvider>
                    </IntentProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/intent/:id"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <IntentProvider>
                      <IntentSocketProvider>
                        <IntentRuleDetails />
                      </IntentSocketProvider>
                    </IntentProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/intent/:id/edit"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <IntentProvider>
                      <IntentSocketProvider>
                        <IntentRuleForm isEdit={true} />
                      </IntentSocketProvider>
                    </IntentProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/intent/analytics"
                element={
                  <ProtectedRoute layout={AdminLayout} requiredRole="admin">
                    <IntentProvider>
                      <IntentSocketProvider>
                        <IntentAnalytics />
                      </IntentSocketProvider>
                    </IntentProvider>
                  </ProtectedRoute>
                }
              />

              {/* Catch all route */}
              <Route
                path="*"
                element={<Navigate to="/" replace />}
              />
            </Routes>

            {/* React Toastify Container */}
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
            </CampaignProvider>
          </SocketProvider>
        </Router>
        </AppSettingsProvider>
      </LicenseErrorProvider>
    </AuthProvider>
  );
}

export default App;